// ActivityDashboardV2 — admin-only preview of the writer-first activity page.
//
// Non-invasive: a NEW file at a NEW route (/dashboard-v2). The public
// /dashboard keeps the review-first layout until GA, when this file replaces
// it and the flag gate + noindex are dropped.
//
// The story matches pricing v2: the Writer is the product, reviews are the
// quality gate inside it.

import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { useWriterEnabled } from '@/hooks/useWriterEnabled';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useWriterProjectsOverview } from '@/hooks/useWriterProjectsOverview';
import { useNeedsYouQueue } from '@/hooks/useNeedsYouQueue';
import { createWriterDraft } from '@/lib/writer/createWriterDraft';
import CommandBar from '@/components/dashboard/v2/CommandBar';
import ContinueWritingCard from '@/components/dashboard/v2/ContinueWritingCard';
import NeedsYouQueue from '@/components/dashboard/v2/NeedsYouQueue';
import DraftRail from '@/components/dashboard/v2/DraftRail';
import WalletPanel from '@/components/dashboard/v2/WalletPanel';
import ReviewDesk from '@/components/dashboard/v2/ReviewDesk';

const ActivityDashboardV2: React.FC = () => {
  const { enabled, loading: flagLoading } = useWriterEnabled();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { projects, loading: projectsLoading } = useWriterProjectsOverview();
  const { items } = useNeedsYouQueue();
  const [creating, setCreating] = useState(false);

  const { data: profile } = useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase.from('users').select('name').eq('id', user.id).single();
      return data;
    },
    enabled: !!user?.id,
  });

  // Projects arrive ordered by updated_at desc — the newest is the resume card.
  const resumeProject = useMemo(() => projects[0] ?? null, [projects]);

  const handleNewDraft = async () => {
    if (!user || creating) return;
    setCreating(true);
    const id = await createWriterDraft(user.id);
    setCreating(false);
    if (!id) {
      toast.error('Could not create draft');
      return;
    }
    navigate(`/write/${id}`);
  };

  if (flagLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!enabled) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-24 text-center">
          <h1 className="text-2xl font-semibold mb-3">Activity is temporarily unavailable</h1>
          <p className="text-muted-foreground">This workspace is being updated. Please check back shortly.</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Activity · PaperVerse</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />

        <main className="flex-1 pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <CommandBar name={profile?.name ?? null} />
            <WalletPanel />
            <ContinueWritingCard project={resumeProject} loading={projectsLoading} />
            <NeedsYouQueue items={items} />
            <DraftRail
              projects={projects}
              loading={projectsLoading}
              onNewDraft={handleNewDraft}
            />
            <ReviewDesk />
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default ActivityDashboardV2;
